import { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Button, Tag, Text, useToastContext } from "@ttc3k/trekker";
import { Box, Flex } from "styled-system/jsx";
import {
  EnumWorkbenchSuggestionStatus,
  WorkbenchSuggestionFieldsFragment,
  useWorkbenchSuggestionAcceptMutation,
  useWorkbenchSuggestionDismissMutation,
} from "gql/generated";
import { WORKBENCH_REFETCH_QUERIES } from "./refetchQueries";
import { WorkbenchMarkdown } from "./WorkbenchMarkdown";

const OPERATION_LABEL: Record<string, string> = {
  append: "Add to section",
  replace: "Replace",
  insertAfter: "Insert after quote",
};

const STATUS_VISUAL: Record<string, "success" | "warning" | "primary" | "destructive"> = {
  pending: "warning",
  accepted: "success",
  applied: "primary",
  dismissed: "destructive",
};

/** Long enough for the status change to read before lists refetch (a dismissed card may then drop out). */
const REFETCH_DELAY_MS = 700;

type ProposedEditCardProps = {
  suggestion: WorkbenchSuggestionFieldsFragment;
  /** Hide the content preview when the card is rendered inline next to the document. */
  compact?: boolean;
  readOnly?: boolean;
};

export const ProposedEditCard = ({ suggestion, compact, readOnly }: ProposedEditCardProps) => {
  const { toastFactory } = useToastContext();
  const apolloClient = useApolloClient();
  const [accept] = useWorkbenchSuggestionAcceptMutation();
  const [dismiss] = useWorkbenchSuggestionDismissMutation();
  // The app's Apollo client defaults mutations to "no-cache", so the card owns its status
  // and switches immediately; a failed request rolls it back.
  const [status, setStatus] = useState<EnumWorkbenchSuggestionStatus>(suggestion.status);
  const [saving, setSaving] = useState<EnumWorkbenchSuggestionStatus | null>(null);
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => setStatus(suggestion.status), [suggestion.status]);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const pulse = () => {
    setFlash(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 260);
  };

  const decide = async (
    next: EnumWorkbenchSuggestionStatus.Accepted | EnumWorkbenchSuggestionStatus.Dismissed,
  ) => {
    const previous = status;
    setStatus(next);
    setSaving(next);
    pulse();
    try {
      const variables = { _id: suggestion._id };
      if (next === EnumWorkbenchSuggestionStatus.Accepted) await accept({ variables });
      else await dismiss({ variables });
      setTimeout(() => {
        void apolloClient.refetchQueries({
          include: [...WORKBENCH_REFETCH_QUERIES, "WorkbenchMessagesByChat"],
        });
      }, REFETCH_DELAY_MS);
    } catch (err) {
      setStatus(previous);
      pulse();
      toastFactory.error({ title: err instanceof Error ? err.message : "Could not update the edit" });
    } finally {
      setSaving(null);
    }
  };

  const edit = suggestion.edit;
  const isPending = status === "pending";
  const isDismissed = status === "dismissed";
  const isReplace = edit?.operation === "replace";
  const showActions = !readOnly && (isPending || status === "accepted");

  return (
    <Box
      borderWidth="1px"
      borderColor={
        isDismissed
          ? "border.light"
          : status === "accepted"
            ? "border.success"
            : isReplace
              ? "border.warning"
              : "border.success"
      }
      bg={
        isDismissed
          ? "bg.wa.light"
          : status === "accepted"
            ? "bg.success.lightest"
            : isReplace
              ? "bg.warning.lightest"
              : "bg.success.lightest"
      }
      opacity={isDismissed ? 0.6 : 1}
      borderRadius="150"
      p="200"
      transition="background-color 0.3s ease, border-color 0.3s ease, opacity 0.3s ease"
    >
      <Flex flexDir="column" gap="50" mb="100">
        <Text visual="smallSemiBold" textDecoration={isDismissed ? "line-through" : "none"}>
          Edit proposed: {suggestion.target === "docs" ? "Documentation" : "Plan"} ›{" "}
          {edit?.section}
        </Text>
        <Flex alignItems="center" gap="100" flexWrap="wrap">
          {edit && (
            <Tag visual="primary" mode="light">
              {OPERATION_LABEL[edit.operation] ?? edit.operation}
            </Tag>
          )}
          <Box
            display="inline-flex"
            transform={flash ? "scale(1.15)" : "scale(1)"}
            transition="transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)"
          >
            <Tag key={status} visual={STATUS_VISUAL[status] ?? "primary"} mode="light">
              {status}
            </Tag>
          </Box>
          {suggestion.createdByName && (
            <Text visual="smallRegular" color="text.light">
              {suggestion.createdByName}
            </Text>
          )}
        </Flex>
      </Flex>
      {edit?.anchorText && (
        <Text visual="smallRegular" color="text.mid" mb="100">
          {isReplace ? "Replaces" : "After"}: “{edit.anchorText}”
        </Text>
      )}
      {!compact && edit && (
        <Box bg="bg.wa.lightest" borderRadius="100" p="200" mb="100">
          <WorkbenchMarkdown>{edit.content}</WorkbenchMarkdown>
        </Box>
      )}
      {compact && edit && (
        <Box mb="100">
          <WorkbenchMarkdown>{edit.content}</WorkbenchMarkdown>
        </Box>
      )}
      {suggestion.body && (
        <Text visual="smallRegular" color="text.mid" mb="100">
          {suggestion.body}
        </Text>
      )}
      <Box
        overflow={showActions ? "visible" : "hidden"}
        py="1px"
        maxH={showActions ? "60px" : "0"}
        opacity={showActions ? 1 : 0}
        transition="max-height 0.3s ease, opacity 0.2s ease"
      >
        <Flex gap="100" justifyContent="flex-end">
          <Button
            size="sm"
            visual="destructive"
            mode="light"
            width="auto"
            isLoading={saving === EnumWorkbenchSuggestionStatus.Dismissed}
            disabled={!!saving || !showActions}
            onClick={() => void decide(EnumWorkbenchSuggestionStatus.Dismissed)}
          >
            Dismiss
          </Button>
          {isPending && (
            <Button
              size="sm"
              visual="success"
              mode="dark"
              width="auto"
              isLoading={saving === EnumWorkbenchSuggestionStatus.Accepted}
              disabled={!!saving}
              onClick={() => void decide(EnumWorkbenchSuggestionStatus.Accepted)}
            >
              Accept
            </Button>
          )}
        </Flex>
      </Box>
    </Box>
  );
};
